import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, Card, Carousel, Icon } from '@design-manager-inc/ui';
import { usePostItemsStatus } from 'api/hooks/usePostItemsStatus';
import { type TItemStatuses } from 'api/resources/items';
import { motion, AnimatePresence } from 'framer-motion';
import { ItemsTop, ItemCard, ItemActionHeader } from './partials';
import { useSearchParams } from 'react-router-dom';
import { type IItem } from 'api/resources/items';
import { EStatus } from 'app/ui/types/core';
import { useAuth } from 'app/features/auth/useAuth';
import { useLayout } from 'app/features/Layout/hooks/useLayout';
import { useGetItemsLayout } from 'api/hooks/useGetItemsLayout';
import { usePageViewed } from 'app/features/Analytics/hooks/usePageViewed';
import {
	PAGES,
	TRACK_ACTION_TYPES,
} from 'app/features/Analytics/utils/constants';
import { track } from 'app/features/Analytics/AnalyticsUtils';

export const CustomModalTitle = ({ title }: { title: string }) => (
	<h2 className="flex text-sm font-sans font-semibold text-left py-[10px]">
		{title}
	</h2>
);
export interface IItemsToUpdate {
	id: string;
	status: TItemStatuses;
}

export const Items = () => {
	usePageViewed(PAGES.ITEMS);
	const [selectedItem, setSelectedItem] = useState<IItem | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedItems, setSelectedItems] = useState<string[]>([]);
	const [filter, setFilter] = useState<TItemStatuses | 'all'>('all');
	const [bulkAction] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';
	const { org } = useAuth();

	const { data } = useGetItemsLayout({ token });

	const items = useMemo(
		() =>
			data?.flatMap((location) =>
				location.groups.flatMap((group) =>
					group.items.map((item) => ({
						...item,
					})),
				),
			),
		[data],
	);

	const { mutate: postItemsStatus } = usePostItemsStatus(token);

	useLayout(
		{
			header: {
				title: `Items ${org?.project?.name ? `for ${org?.project?.name}` : ''}`,
				icon: 'queue-list',
				actions: org?.itemsApproval
					? [
							<ItemActionHeader
								key="item-action-header"
								items={items || []}
								filter={filter}
								setFilter={setFilter}
								isFilterDropdownOpen={isFilterDropdownOpen}
								setIsFilterDropdownOpen={setIsFilterDropdownOpen}
							/>,
						]
					: [],
			},
		},
		useMemo(
			() => [
				org?.project?.name,
				org?.itemsApproval,
				items,
				isFilterDropdownOpen,
				filter,
			],
			[
				org?.project?.name,
				org?.itemsApproval,
				items,
				isFilterDropdownOpen,
				filter,
			],
		),
	);

	const filteredItems = useMemo(
		() =>
			data?.flatMap((location) =>
				location.groups.flatMap((group) =>
					group.items.filter((item: IItem) => {
						const matchesFilter = filter === 'all' || filter === item.status;

						const matchesSearch = searchTerm
							? (item.title
									?.toLowerCase()
									?.includes(searchTerm.toLowerCase()) ??
									false) ||
								(item.description
									?.toLowerCase()
									?.includes(searchTerm.toLowerCase()) ??
									false)
							: true;

						return matchesFilter && matchesSearch;
					}),
				),
			),
		[data, filter, searchTerm],
	);

	useEffect(() => {
		setSelectedItems([]);
	}, [filter, searchTerm]);

	const handleSelectAll = () => {
		track('All Items selected', {
			'Action Type': TRACK_ACTION_TYPES.CLICK,
			'Screen Name': `${PAGES.ITEMS} Page`,
		});

		setSelectedItems(
			searchTerm || filter
				? filteredItems.map((item: IItem) => item.id)
				: items?.map((item: IItem) => item.id) || [],
		);
	};

	const openModal = useCallback((item: IItem, imageIndex: number = 0) => {
		setSelectedItem(item);
		setSelectedImageIndex(imageIndex);
		setIsModalOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setSelectedItem(null);
		setSelectedImageIndex(0);
	}, []);

	const toggleItemSelection = useCallback((itemId: string) => {
		setSelectedItems((prev) =>
			prev.includes(itemId)
				? prev.filter((id) => id !== itemId)
				: [...prev, itemId],
		);
	}, []);

	const handleButtonGroupChange = useCallback(
		(itemId: string, action: TItemStatuses | null) => {
			if (action) {
				postItemsStatus([{ id: itemId, status: action }], {
					onSettled: () => {
						if (action === EStatus.Approved) {
							track('Item Approved', {
								'Action Type': TRACK_ACTION_TYPES.SAVE,
								'Screen Name': `${PAGES.ITEMS} Page`,
							});
						} else if (action === EStatus.Rejected) {
							track('Item Declined', {
								'Action Type': TRACK_ACTION_TYPES.SAVE,
								'Screen Name': `${PAGES.ITEMS} Page`,
							});
						}
					},
				});
			}
		},
		[postItemsStatus],
	);

	const handleUndo = useCallback(
		(itemId: string) => {
			postItemsStatus([{ id: itemId, status: EStatus.NotSet }]);
		},
		[postItemsStatus],
	);

	const handleApproveAll = useCallback(() => {
		if (selectedItems.length === 0) return;

		const itemsToUpdate: IItemsToUpdate[] = selectedItems.map((itemId) => ({
			id: itemId,
			status: 'approved' as TItemStatuses,
		}));

		postItemsStatus(itemsToUpdate, {
			onSuccess: () => {
				setSelectedItems([]);
			},
			onSettled: () => {
				track('Items Accepted', {
					'Action Type': TRACK_ACTION_TYPES.SAVE,
					'Screen Name': `${PAGES.ITEMS} Page`,
				});
			},
		});
	}, [selectedItems, postItemsStatus]);

	const handleDeclineAll = useCallback(() => {
		if (selectedItems.length === 0) return;

		const itemsToUpdate: IItemsToUpdate[] = selectedItems.map((itemId) => ({
			id: itemId,
			status: 'Rejected' as TItemStatuses,
		}));

		postItemsStatus(itemsToUpdate, {
			onSuccess: () => {
				setSelectedItems([]);
			},
			onSettled: () => {
				track('Items Declined', {
					'Action Type': TRACK_ACTION_TYPES.SAVE,
					'Screen Name': `${PAGES.ITEMS} Page`,
				});
			},
		});
	}, [selectedItems, postItemsStatus]);

	const locationsToRender = useMemo(
		() =>
			data?.map((location) => {
				// Filter groups that have at least one filtered item
				const filteredGroups = location.groups
					.map((group) => {
						// Filter group's items to only those in filteredItems
						const filteredGroupItems = group.items.filter((item) =>
							filteredItems.some((fi) => fi.id === item.id),
						);

						return filteredGroupItems.length > 0
							? { ...group, items: filteredGroupItems }
							: null;
					})
					.filter(Boolean); // remove null groups

				if (filteredGroups.length === 0) return null; // skip location if no groups

				const locationName = location.locationName.trim();

				return (
					<div key={location.locationName} className="ml-4">
						{locationName && (
							<span className="ml-4 font-bold">{locationName} Location</span>
						)}
						{filteredGroups.map((group) => (
							<div
								key={group?.groupName}
								className="ml-12 mt-2 flex gap-4 flex-col"
							>
								{group?.groupName?.trim() && (
									<span className="font-bold">
										{group?.groupName.trim()} Group
									</span>
								)}
								{group?.items.map((item) => (
									<div key={item.id} className="ml-12">
										<ItemCard
											key={item.id}
											item={item}
											isSelected={selectedItems.includes(item.id)}
											onSelect={toggleItemSelection}
											onImageClick={(imageIndex) => openModal(item, imageIndex)}
											onButtonGroupChange={handleButtonGroupChange}
											onUndo={handleUndo}
											itemsApproval={org?.itemsApproval}
										/>
									</div>
								))}
							</div>
						))}
					</div>
				);
			}),
		[
			data,
			filteredItems,
			selectedItems,
			toggleItemSelection,
			openModal,
			handleButtonGroupChange,
			handleUndo,
			org?.itemsApproval,
		],
	);

	return items?.length > 0 ? (
		<>
			<Card
				border="2xl"
				elevation={2}
				className="bg-background mx-auto px-4 lg:px-10 py-4 lg:py-6"
			>
				<ItemsTop
					showSelectAll={!!org?.itemsApproval && items?.length > 0}
					handleSelectAll={handleSelectAll}
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					itemCount={filteredItems.length}
					selectedItemsCount={selectedItems.length}
					onDeclineAll={handleDeclineAll}
					onApproveAll={handleApproveAll}
					isOpen={bulkAction}
				/>

				<AnimatePresence>
					<motion.div className="space-y-4 text-fix">
						{locationsToRender}
					</motion.div>
				</AnimatePresence>
			</Card>

			{selectedItem && (
				<Modal
					isOpen={isModalOpen}
					onClose={closeModal}
					title={selectedItem.title}
					overrides={{
						Title: CustomModalTitle,
					}}
				>
					<Carousel
						displayRest={selectedItem.images.length > 1 ? 'images' : 'none'}
						images={selectedItem.images.map((imageId: string) => ({
							src: `${process.env.PUBLIC_APP_API_BASE_URL}/file/${token}/${imageId}?maxHeight=640`,
							alt: '',
						}))}
						initialSlide={selectedImageIndex}
					/>
				</Modal>
			)}
		</>
	) : (
		<Card
			elevation={2}
			className="h-full flex flex-col rounded-xl p-4 bg-background w-fit md:rounded-3xl lg:rounded-3xl md:p-5 lg:p-5"
		>
			<div className="h-full gap-1 flex flex-col items-center text-center">
				<Icon
					name="check-circle"
					size="lg"
					className="text-primary mb-4 fill-primary-badge"
				/>

				<h3 className="text-lg font-semibold text-text">No Items To Display</h3>
				<p className="text-sm text-gray-500">
					Check back later for new updates or tasks to complete.
				</p>
			</div>
		</Card>
	);
};

Items.displayName = 'Items';
